import { Router, Route, route } from 'preact-router';
import { useEffect } from 'preact/hooks';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { OAuthCallbackPage } from './pages/auth/OAuthCallbackPage';
import { ProfilePage } from './pages/settings/ProfilePage';
import { AgentChatPage } from './pages/agents/AgentChatPage';
import { AgentThreadsPage } from './pages/agents/AgentThreadsPage';
import { AgentSettingsPage } from './pages/agents/AgentSettingsPage';
import { ConnectionsPage } from './pages/connections/ConnectionsPage';
import { ConnectionDetailsPage } from './pages/connections/ConnectionDetailsPage';
import { ConnectorRegistryPage } from './pages/connectors/ConnectorRegistryPage';
import { TeamsPage } from './pages/teams/TeamsPage';
import { TeamDetailsPage } from './pages/teams/TeamDetailsPage';
import { TeamSettingsPage } from './pages/teams/TeamSettingsPage';
import { TeamInvitePage } from './pages/teams/TeamInvitePage';
import { TeamMembersPage } from './pages/teams/TeamMembersPage';
import { TeamFilesPage } from './pages/team-files/TeamFilesPage';
import { SkillsPage } from './pages/skills/SkillsPage';
import { BillingPage } from './pages/billing/BillingPage';
import { InvitationsPage } from './pages/invitations/InvitationsPage';
import { OnboardingPage } from './pages/onboarding/OnboardingPage';
import { ScopeProvider } from './contexts/ScopeContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { WebSocketRouter } from './components/WebSocketRouter';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AmplifyAuthWrapper } from './components/AmplifyAuthWrapper';
import { isCognitoEnabled } from './amplify-config';

// Redirect component - used to redirect auth routes to home when Cognito is enabled
function RedirectToHome() {
  useEffect(() => {
    route('/', true);
  }, []);

  return (
    <div class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <span class="loading loading-spinner loading-lg"></span>
        <p class="mt-4">Redirecting...</p>
      </div>
    </div>
  );
}

// Protected route component
function PrivateRoute({ component: Component, ...props }: any) {
  const { user, isLoading } = useAuth();

  useEffect(() => {
    // Only redirect when loading is complete and user is not authenticated
    if (!isLoading && !user && !isCognitoEnabled) {
      // Store the attempted URL for redirect after login
      const currentPath = window.location.pathname;
      if (currentPath !== '/login') {
        sessionStorage.setItem('redirectAfterLogin', currentPath);
      }
      route('/login', true);
    }
  }, [user, isLoading]);

  if (isLoading) {
    return (
      <div class="min-h-screen flex items-center justify-center">
        <div class="text-center">
          <span class="loading loading-spinner loading-lg"></span>
          <p class="mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // When using Cognito, Amplify Authenticator ensures user is authenticated
  // If no user and Cognito is enabled, show loading (Amplify should handle auth)
  if (!user) {
    return (
      <div class="min-h-screen flex items-center justify-center">
        <div class="text-center">
          <span class="loading loading-spinner loading-lg"></span>
          <p class="mt-4">Authenticating...</p>
        </div>
      </div>
    );
  }

  return <Component {...props} />;
}

// Team route component that provides scope context
function TeamRoute({ component: Component, ...props }: any) {
  const { user, isLoading } = useAuth();

  useEffect(() => {
    // Only redirect when loading is complete and user is not authenticated
    if (!isLoading && !user && !isCognitoEnabled) {
      // Store the attempted URL for redirect after login
      const currentPath = window.location.pathname;
      sessionStorage.setItem('redirectAfterLogin', currentPath);
      route('/login', true);
    }
  }, [user, isLoading]);

  if (isLoading) {
    return (
      <div class="min-h-screen flex items-center justify-center">
        <div class="text-center">
          <span class="loading loading-spinner loading-lg"></span>
          <p class="mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // When using Cognito, Amplify Authenticator ensures user is authenticated
  // If no user and Cognito is enabled, show loading
  if (!user) {
    return (
      <div class="min-h-screen flex items-center justify-center">
        <div class="text-center">
          <span class="loading loading-spinner loading-lg"></span>
          <p class="mt-4">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Extract teamId from URL (works for both /teams/:teamId and /t/:teamId)
  const path = window.location.pathname;
  let teamId: string | null = null;

  // Try /teams/:teamId pattern first
  const teamsMatch = path.match(/^\/teams\/([^\/]+)/);
  if (teamsMatch) {
    teamId = teamsMatch[1];
  } else {
    // Try legacy /t/:teamId pattern
    const legacyMatch = path.match(/^\/t\/([^\/]+)/);
    if (legacyMatch) {
      teamId = legacyMatch[1];
    }
  }

  if (!teamId) {
    return (
      <div class="min-h-screen flex items-center justify-center">
        <div class="text-center">
          <h1 class="text-xl font-bold text-error">Team Not Found</h1>
          <p class="mt-2">Unable to extract team ID from URL</p>
        </div>
      </div>
    );
  }

  return (
    <ScopeProvider scope="team" teamId={teamId} userId={user.id || user.sub}>
      <WebSocketProvider>
        <WebSocketRouter>
          <Component {...props} />
        </WebSocketRouter>
      </WebSocketProvider>
    </ScopeProvider>
  );
}

// App router component
function AppRouter() {
  // Conditionally build route list based on auth mode
  const authRoutes = !isCognitoEnabled ? [
    // Sandbox mode - use custom auth pages
    <Route key="login1" path="/login" component={LoginPage} />,
    <Route key="login2" path="/auth/login" component={LoginPage} />,
    <Route key="signup1" path="/signup" component={SignupPage} />,
    <Route key="signup2" path="/auth/signup" component={SignupPage} />,
    <Route key="verify" path="/auth/verify" component={VerifyEmailPage} />,
    <Route key="callback" path="/auth/callback" component={OAuthCallbackPage} />,
  ] : [
    // Cognito mode - redirect auth routes to home
    <Route key="login1" path="/login" component={RedirectToHome} />,
    <Route key="login2" path="/auth/login" component={RedirectToHome} />,
    <Route key="signup1" path="/signup" component={RedirectToHome} />,
    <Route key="signup2" path="/auth/signup" component={RedirectToHome} />,
    <Route key="verify" path="/auth/verify" component={RedirectToHome} />,
    <Route key="callback" path="/auth/callback" component={RedirectToHome} />,
  ];

  console.log('🛣️ Router initialized with Cognito:', isCognitoEnabled);
  console.log('🛣️ Auth routes mode:', isCognitoEnabled ? 'REDIRECT (Cognito mode)' : 'CUSTOM (sandbox mode)');

  return (
    <Router>
      {/* Auth Routes */}
      {authRoutes}

      {/* Homepage - Redirect to team view */}
      <PrivateRoute path="/" component={TeamsPage} />

      {/* Onboarding Route */}
      <PrivateRoute path="/onboarding" component={OnboardingPage} />

      {/* Team Routes */}
      <PrivateRoute path="/teams" component={TeamsPage} />
      <PrivateRoute path="/teams/create" component={TeamsPage} />
      <TeamRoute path="/teams/:teamId" component={TeamDetailsPage} />
      <TeamRoute path="/teams/:teamId/settings" component={TeamSettingsPage} />
      <TeamRoute path="/teams/:teamId/invite" component={TeamInvitePage} />
      <TeamRoute path="/teams/:teamId/members" component={TeamMembersPage} />
      <TeamRoute path="/teams/:teamId/billing" component={BillingPage} />
      <TeamRoute path="/teams/:teamId/connections" component={ConnectionsPage} />
      <TeamRoute path="/teams/:teamId/connections/:connectionId" component={ConnectionDetailsPage} />
      <TeamRoute path="/teams/:teamId/connectors" component={ConnectorRegistryPage} />
      <TeamRoute path="/teams/:teamId/files" component={TeamFilesPage} />
      <TeamRoute path="/teams/:teamId/skills" component={SkillsPage} />
      <TeamRoute path="/teams/:teamId/chat/:agentId/:threadId" component={AgentChatPage} />
      <TeamRoute path="/teams/:teamId/chat/:agentId" component={AgentChatPage} />
      <TeamRoute path="/teams/:teamId/agents/:agentId/threads" component={AgentThreadsPage} />
      <TeamRoute path="/teams/:teamId/:agentId/settings" component={AgentSettingsPage} />

      {/* Workflow Routes - using AgentChatPage for inline workflows */}
      <TeamRoute path="/teams/:teamId/workflows/:agentId" component={AgentChatPage} />
      <TeamRoute path="/teams/:teamId/workflows/:agentId/new" component={AgentChatPage} />
      <TeamRoute path="/teams/:teamId/workflows/:agentId/:workflowId/:threadId" component={AgentChatPage} />
      <TeamRoute path="/teams/:teamId/workflows/:agentId/:workflowId" component={AgentChatPage} />

      {/* Legacy routes without /agents/ prefix */}
      <TeamRoute path="/teams/:teamId/:agentId/threads" component={AgentThreadsPage} />

      {/* User profile */}
      <PrivateRoute path="/invitations" component={InvitationsPage} />
      <PrivateRoute path="/profile" component={ProfilePage} />

      {/* Default/catch-all route - redirect to home */}
      <PrivateRoute default component={TeamsPage} />

    </Router>
  );
}

// Main App component
export function App() {
  return (
    <AmplifyAuthWrapper>
      <AuthProvider>
        <AppRouter />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          aria-label="Notifications"
        />
      </AuthProvider>
    </AmplifyAuthWrapper>
  );
}
